export const BIBLE_TOTAL_CHAPTERS = 1189 as const;

export type BibleTestament = 'old' | 'new';

export const BIBLE_CANON = [
  ['GEN', 'Gênesis', 'old', 50], ['EXO', 'Êxodo', 'old', 40], ['LEV', 'Levítico', 'old', 27],
  ['NUM', 'Números', 'old', 36], ['DEU', 'Deuteronômio', 'old', 34], ['JOS', 'Josué', 'old', 24],
  ['JDG', 'Juízes', 'old', 21], ['RUT', 'Rute', 'old', 4], ['1SA', '1 Samuel', 'old', 31],
  ['2SA', '2 Samuel', 'old', 24], ['1KI', '1 Reis', 'old', 22], ['2KI', '2 Reis', 'old', 25],
  ['1CH', '1 Crônicas', 'old', 29], ['2CH', '2 Crônicas', 'old', 36], ['EZR', 'Esdras', 'old', 10],
  ['NEH', 'Neemias', 'old', 13], ['EST', 'Ester', 'old', 10], ['JOB', 'Jó', 'old', 42],
  ['PSA', 'Salmos', 'old', 150], ['PRO', 'Provérbios', 'old', 31], ['ECC', 'Eclesiastes', 'old', 12],
  ['SNG', 'Cântico dos Cânticos', 'old', 8], ['ISA', 'Isaías', 'old', 66], ['JER', 'Jeremias', 'old', 52],
  ['LAM', 'Lamentações', 'old', 5], ['EZK', 'Ezequiel', 'old', 48], ['DAN', 'Daniel', 'old', 12],
  ['HOS', 'Oseias', 'old', 14], ['JOL', 'Joel', 'old', 3], ['AMO', 'Amós', 'old', 9],
  ['OBA', 'Obadias', 'old', 1], ['JON', 'Jonas', 'old', 4], ['MIC', 'Miqueias', 'old', 7],
  ['NAM', 'Naum', 'old', 3], ['HAB', 'Habacuque', 'old', 3], ['ZEP', 'Sofonias', 'old', 3],
  ['HAG', 'Ageu', 'old', 2], ['ZEC', 'Zacarias', 'old', 14], ['MAL', 'Malaquias', 'old', 4],
  ['MAT', 'Mateus', 'new', 28], ['MRK', 'Marcos', 'new', 16], ['LUK', 'Lucas', 'new', 24],
  ['JHN', 'João', 'new', 21], ['ACT', 'Atos', 'new', 28], ['ROM', 'Romanos', 'new', 16],
  ['1CO', '1 Coríntios', 'new', 16], ['2CO', '2 Coríntios', 'new', 13], ['GAL', 'Gálatas', 'new', 6],
  ['EPH', 'Efésios', 'new', 6], ['PHP', 'Filipenses', 'new', 4], ['COL', 'Colossenses', 'new', 4],
  ['1TH', '1 Tessalonicenses', 'new', 5], ['2TH', '2 Tessalonicenses', 'new', 3], ['1TI', '1 Timóteo', 'new', 6],
  ['2TI', '2 Timóteo', 'new', 4], ['TIT', 'Tito', 'new', 3], ['PHM', 'Filemom', 'new', 1],
  ['HEB', 'Hebreus', 'new', 13], ['JAS', 'Tiago', 'new', 5], ['1PE', '1 Pedro', 'new', 5],
  ['2PE', '2 Pedro', 'new', 3], ['1JN', '1 João', 'new', 5], ['2JN', '2 João', 'new', 1],
  ['3JN', '3 João', 'new', 1], ['JUD', 'Judas', 'new', 1], ['REV', 'Apocalipse', 'new', 22]
] as const satisfies readonly (readonly [string, string, BibleTestament, number])[];

export type BibleBookCode = typeof BIBLE_CANON[number][0];
export type BibleChapterId = `${BibleBookCode}:${number}`;

export interface BibleCanonBook {
  code: BibleBookCode;
  name: string;
  testament: BibleTestament;
  chapters: number;
}

export const BIBLE_BOOKS: readonly BibleCanonBook[] = BIBLE_CANON.map(([code, name, testament, chapters]) => ({
  code, name, testament, chapters
}));

export const CANONICAL_BIBLE_BOOK_ORDER: readonly BibleBookCode[] = BIBLE_BOOKS.map(({ code }) => code);

export const isValidBibleBookOrder = (order: readonly string[]): order is readonly BibleBookCode[] =>
  order.length === CANONICAL_BIBLE_BOOK_ORDER.length
  && new Set(order).size === CANONICAL_BIBLE_BOOK_ORDER.length
  && order.every((code) => CANONICAL_BIBLE_BOOK_ORDER.includes(code as BibleBookCode));

export const validateBibleBookOrder = (order: readonly string[]): BibleBookCode[] => {
  if (!isValidBibleBookOrder(order)) {
    throw new Error('O plano deve conter os 66 livros bíblicos, sem duplicatas ou ausências.');
  }
  return [...order];
};

export const resolveBibleBookOrder = (order?: readonly string[]): BibleBookCode[] =>
  order === undefined ? [...CANONICAL_BIBLE_BOOK_ORDER] : validateBibleBookOrder(order);

export const ALL_BIBLE_CHAPTER_IDS: readonly BibleChapterId[] = BIBLE_BOOKS.flatMap((book) =>
  Array.from({ length: book.chapters }, (_, index) => `${book.code}:${index + 1}` as BibleChapterId));

const bookByCode = new Map(BIBLE_BOOKS.map((book, index) => [book.code, { ...book, index }]));

export const getBibleBook = (code: string): BibleCanonBook | undefined => bookByCode.get(code as BibleBookCode);

export const makeBibleChapterId = (code: BibleBookCode, chapter: number): BibleChapterId => {
  const book = getBibleBook(code);
  if (!book || !Number.isInteger(chapter) || chapter < 1 || chapter > book.chapters) {
    throw new Error('Capítulo bíblico inválido.');
  }
  return `${code}:${chapter}`;
};

export const parseBibleChapterId = (value: string): { code: BibleBookCode; chapter: number } | undefined => {
  const match = /^([1-3]?[A-Z]{2,3}):(\d{1,3})$/.exec(value);
  if (!match) return undefined;
  const code = match[1] as BibleBookCode;
  const chapter = Number(match[2]);
  const book = getBibleBook(code);
  return book && Number.isInteger(chapter) && chapter >= 1 && chapter <= book.chapters ? { code, chapter } : undefined;
};

export const isBibleChapterId = (value: string): value is BibleChapterId => Boolean(parseBibleChapterId(value));

export const compareBibleChapterIds = (left: BibleChapterId, right: BibleChapterId): number => {
  const a = parseBibleChapterId(left)!;
  const b = parseBibleChapterId(right)!;
  return bookByCode.get(a.code)!.index - bookByCode.get(b.code)!.index || a.chapter - b.chapter;
};

export const normalizeBibleChapterIds = (ids: readonly string[]): BibleChapterId[] => {
  if (ids.some((id) => !isBibleChapterId(id))) throw new Error('A seleção contém um capítulo bíblico inválido.');
  return [...new Set(ids as readonly BibleChapterId[])].sort(compareBibleChapterIds);
};

export const bibleChapterRange = (code: BibleBookCode, startChapter: number, endChapter: number): BibleChapterId[] => {
  if (!Number.isInteger(startChapter) || !Number.isInteger(endChapter) || endChapter < startChapter) {
    throw new Error('Informe um intervalo de capítulos válido.');
  }
  return Array.from({ length: endChapter - startChapter + 1 }, (_, index) => makeBibleChapterId(code, startChapter + index));
};

export const formatBibleChapterRange = (ids: readonly BibleChapterId[]): string => {
  const normalized = normalizeBibleChapterIds(ids);
  if (!normalized.length) return 'Nenhum capítulo';
  const first = parseBibleChapterId(normalized[0]!)!;
  const sameBook = normalized.every((id) => parseBibleChapterId(id)?.code === first.code);
  if (!sameBook) return normalized.map((id) => {
    const parsed = parseBibleChapterId(id)!;
    return `${getBibleBook(parsed.code)!.name} ${parsed.chapter}`;
  }).join(', ');
  const last = parseBibleChapterId(normalized.at(-1)!)!;
  return `${getBibleBook(first.code)!.name} ${first.chapter}${last.chapter === first.chapter ? '' : `–${last.chapter}`}`;
};

export const nextBibleChapter = (id: BibleChapterId): BibleChapterId | undefined => {
  const parsed = parseBibleChapterId(id)!;
  const current = bookByCode.get(parsed.code)!;
  if (parsed.chapter < current.chapters) return makeBibleChapterId(parsed.code, parsed.chapter + 1);
  const next = BIBLE_BOOKS[current.index + 1];
  return next ? makeBibleChapterId(next.code, 1) : undefined;
};
